import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Toaster } from "./components/ui/sonner";
import { CompareProvider } from "./context/CompareContext";
import { AuthProvider } from "./lib/AuthContext";
import HomePage from "./pages/HomePage";
import SearchResults from "./pages/SearchResults";
import SearchByName from "./pages/SearchByName";
import SearchByNPI from "./pages/SearchByNPI";
import ProviderProfile from "./pages/ProviderProfile";
import SpecialtiesLanding from "./pages/SpecialtiesLanding";
import SpecialtyDirectory from "./pages/SpecialtyDirectory";
import SpecialtyStateDirectory from "./pages/SpecialtyStateDirectory";
import SpecialtyCityDirectory from "./pages/SpecialtyCityDirectory";
import StatesLanding from "./pages/StatesLanding";
import StateDirectory from "./pages/StateDirectory";
import CityDirectory from "./pages/CityDirectory";
import NearMePage from "./pages/NearMePage";
import ComparePage from "./pages/ComparePage";
import DeaNpiLookup from "./pages/DeaNpiLookup";
import VerificationReport from "./pages/VerificationReport";
import FindADoctor from "./pages/FindADoctor";
import SitemapDirectory from "./pages/SitemapDirectory";
import PecosLookup from "./pages/PecosLookup";
import OigExclusionLookup from "./pages/OigExclusionLookup";
import BulkScreening from "./pages/BulkScreening";
import RecentlyVerified from "./pages/RecentlyVerified";
import Dashboard from "./pages/Dashboard";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSignups from "./pages/admin/AdminSignups";
import AdminActivity from "./pages/admin/AdminActivity";
import AdminEmail from "./pages/admin/AdminEmail";
import AdminSettings from "./pages/admin/AdminSettings";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardOverview from "./pages/dashboard/DashboardOverview";
import DashboardHistory from "./pages/dashboard/DashboardHistory";
import DashboardSaved from "./pages/dashboard/DashboardSaved";
import DashboardMonitoring from "./pages/dashboard/DashboardMonitoring";
import DashboardBulk from "./pages/dashboard/DashboardBulk";
import DashboardRoster from "./pages/dashboard/DashboardRoster";
import DashboardSubscription from "./pages/dashboard/DashboardSubscription";
import DashboardSettingsPage from "./pages/dashboard/DashboardSettings";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CompareBar from "./components/CompareBar";
import GTMPageViewTracker from "./components/GTMPageViewTracker";
import "./App.css";

function AppRouter() {
  return (
    <>
      <GTMPageViewTracker />
      <Routes>
      {/* Admin routes — own layout, no Header/Footer */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="signups" element={<AdminSignups />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="email" element={<AdminEmail />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Dashboard routes — own sidebar layout */}
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<DashboardOverview />} />
        <Route path="history" element={<DashboardHistory />} />
        <Route path="saved" element={<DashboardSaved />} />
        <Route path="monitoring" element={<DashboardMonitoring />} />
        <Route path="bulk" element={<DashboardBulk />} />
        <Route path="reconcile" element={<DashboardRoster />} />
        <Route path="subscription" element={<DashboardSubscription />} />
        <Route path="settings" element={<DashboardSettingsPage />} />
      </Route>

      {/* Public routes — standard layout */}
      <Route path="*" element={<PublicLayout />} />
    </Routes>
    </>
  );
}

function PublicLayout() {
  return (
    <>
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/search/name/:name" element={<SearchByName />} />
          <Route path="/search/npi/:npi" element={<SearchByNPI />} />
          <Route path="/near-me" element={<NearMePage />} />
          <Route path="/near-me/:postalCode" element={<NearMePage />} />
          <Route path="/near-me/:postalCode/:radius" element={<NearMePage />} />
          <Route path="/provider/:npi/:slug?" element={<ProviderProfile />} />
          <Route path="/specialties" element={<SpecialtiesLanding />} />
          <Route path="/states" element={<StatesLanding />} />
          <Route path="/specialty/:specialty" element={<SpecialtyDirectory />} />
          <Route path="/specialty/:specialty/:stateCode" element={<SpecialtyStateDirectory />} />
          {/* Programmatic specialty × city landing pages for local SEO */}
          {/* URL ex: /providers/family-medicine-in-bend-or — parsed inside the component */}
          <Route path="/providers/:slug" element={<SpecialtyCityDirectory />} />
          <Route path="/state/:stateCode" element={<StateDirectory />} />
          <Route path="/state/:stateCode/:city" element={<CityDirectory />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/dea-npi-lookup" element={<DeaNpiLookup />} />
          <Route path="/verify" element={<VerificationReport />} />
          <Route path="/find-a-doctor" element={<FindADoctor />} />
          <Route path="/find-a-doctor/:termSlug" element={<FindADoctor />} />
          <Route path="/find-a-doctor/:termSlug/:stateSlug" element={<FindADoctor />} />
          <Route path="/find-a-doctor/:termSlug/:stateSlug/:citySlug" element={<FindADoctor />} />
          <Route path="/directory" element={<SitemapDirectory />} />
          <Route path="/pecos-lookup" element={<PecosLookup />} />
          <Route path="/pecos-lookup/npi/:npiParam" element={<PecosLookup />} />
          <Route path="/pecos-lookup/name/:lastName" element={<PecosLookup />} />
          <Route path="/pecos-lookup/name/:lastName/:firstName" element={<PecosLookup />} />
          <Route path="/oig-exclusion-lookup" element={<OigExclusionLookup />} />
          <Route path="/oig-exclusion-lookup/npi/:npiParam" element={<OigExclusionLookup />} />
          <Route path="/bulk-screening" element={<BulkScreening />} />
          <Route path="/recently-verified" element={<RecentlyVerified />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Routes>
      </main>
      <Footer />
      <CompareBar />
    </>
  );
}

function CallbackPage() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const errorDesc = params.get("error_description");
  const { isAuthenticated, isLoading } = useAuth0();

  // Redirect as soon as Auth0 confirms authentication, or after 3s safety net
  useEffect(() => {
    if (error) return;
    if (isAuthenticated && !isLoading) {
      window.location.href = "/dashboard";
      return;
    }
    const timer = setTimeout(() => {
      window.location.href = "/dashboard";
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, isAuthenticated, isLoading]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-xl font-bold">!</span>
          </div>
          <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Sign in failed</h2>
          <p className="text-sm text-slate-500 mb-4">{errorDesc || "Something went wrong during authentication."}</p>
          <a href="/" className="inline-flex items-center gap-2 px-4 py-2 bg-teal text-white text-sm font-semibold rounded-lg hover:bg-teal/90">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-500 text-sm">Signing you in...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
    >
      <HelmetProvider>
        <AuthProvider>
          <CompareProvider>
            <div className="min-h-screen flex flex-col bg-white">
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
              <Toaster position="bottom-right" />
            </div>
          </CompareProvider>
        </AuthProvider>
      </HelmetProvider>
    </Auth0Provider>
  );
}

export default App;
